import { nanoid } from 'nanoid';
import {
  AddNewTimelineItemOnDropArgs,
  checkForLecturerConflict,
  checkForLocationConflict,
  getTimelineItemDroppedTime,
} from '@modules/timetable';

export const addNewTimelineItemOnDrop = async ({
  dropTarget,
  item,
  group,
  selectedLocation,
  selectedClassId,
  timelineItems,
  timetableSlotCounts,
  setTimelineItems,
  addToast,
}: // eslint-disable-next-line consistent-return
AddNewTimelineItemOnDropArgs): Promise<void> => {
  // Can only add to the selected class
  if (group.id.split('/')[1] !== selectedClassId) {
    return addToast('Error, You can only modify the class you select.', {
      appearance: 'error',
      autoDismiss: true,
    });
  }

  // Cannot drop an item without a lecturer
  if (!item.lecturerID) {
    return addToast('Error, You cannot have a timeline item without a lecturer.', {
      appearance: 'error',
      autoDismiss: true,
    });
  }

  const { startTime, endTime } = getTimelineItemDroppedTime({
    dropTarget,
    duration: item.duration,
    groupId: group.id,
    timelineItems,
  });

  const newTimelineItem = {
    id: `${item.id}/${nanoid()}`,
    groupID: group.id,
    title: item.title,
    startTime,
    endTime,
    duration: item.duration,
    color: item.color,
    elementID: item.elementID || null,
    batchClassID: group.id,
    itemType: item.itemType,
    lecturerID: item.lecturerID || null,
    locationID: selectedLocation,
    subjectName: item.subjectName,
    published: false,
    batchName: null,
    batchClassName: null,
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    removeItem: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    moveItem: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setEndTime: () => {},
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    setStartTime: () => {},
    dbId: null,
  };

  // Check for conflicts
  let allowDrop = true;

  // Check for location conflicts
  if (allowDrop) {
    allowDrop = await checkForLocationConflict({
      timelineItems,
      item: newTimelineItem,
      endTime,
      startTime,
    });
  }

  // Check for lecturer conflicts
  if (allowDrop) {
    allowDrop = await checkForLecturerConflict({
      timelineItems,
      item: newTimelineItem,
      endTime,
      startTime,
    });
  }

  if (allowDrop) {
    // Update available slot count
    const draggedSlotIndex = timetableSlotCounts.findIndex((timetableSlot) => {
      if (timetableSlot.id === item.elementID && timetableSlot.classId === group.id.split('/')[1]) {
        return timetableSlot.id;
      }
      return undefined;
    });

    if (draggedSlotIndex > -1) {
      timetableSlotCounts[draggedSlotIndex].setTimetableSlotCount(
        timetableSlotCounts[draggedSlotIndex].availableSlotCount - 1,
      );
    }

    setTimelineItems([...timelineItems, newTimelineItem]);
  }
};
